import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAutoContracts,
  postAutoContracts,
  getAutoContractsPreview,
  getFinanceContracts,
  cancelContractExtension,
} from '../../../../api/contracts';
import { isPremiumUser } from '../../../../redux/selector/User';
import { getMyAllSquad } from '../../../../redux/selector/Team';
import { showPopupWindow } from '../../../../redux/actions/Popups';
import { POPUPS } from '../../../../service/constants';
import { toastRender } from '../../../../service/toaster';
import { digitFormater } from '../../../../service/functions';
import { simplifiedPosition } from '../../../../service/data';
import { ReactComponent as Crown } from '../../../../assets/img/crown.svg';
import {
  StyledAutoHeader,
  StyledAutoIcon,
  StyledAutoStatus,
  StyledAutoBody,
  StyledAutoSettings,
  StyledAutoField,
  StyledAutoFooter,
  StyledAutoActions,
  StyledAutoResult,
  StyledAutoCard,
  StyledAutoTitle,
  StyledAutoRow,
  StyledAutoLabel,
  StyledAutoSelect,
  StyledAutoToggle,
  StyledAutoButton,
  StyledAutoHint,
  StyledAutoList,
  StyledAutoItem,
  StyledPreviewHeader,
  StyledPreviewTitle,
  StyledPreviewCounts,
  StyledPreviewCount,
  StyledPreviewTableWrap,
  StyledPreviewTable,
  StyledPreviewPlayer,
  StyledPreviewSkippedRow,
  StyledPreviewMood,
  StyledPatronBadge,
} from './autoStyled';

const STRATEGY_BLURBS = {
  0: 'Lowest wage first, then fewer promised matches.',
  1: 'Fewer promised matches first, then the lowest wage.',
};

const SKIP_COPY = {
  hates_club: "isn't happy here and won't talk",
  unreachable: 'has no deal Chris could agree within his limits',
  cap_space: 'would take the club over the wage cap',
  offer_pending: "has the manager's own offer waiting",
  retiring: 'is retiring',
  already_agreed: 'already has an extension agreed',
  cancelled: 'had a Chris deal you cancelled',
  not_final_season: 'is not in the last year of his deal',
  not_at_club: 'is no longer with the club',
  loaned_in: 'is on loan from another club',
  missing_player_data: "Chris couldn't work out terms",
  illegal_terms: "Chris couldn't work out terms",
  unknown_strategy: "Chris couldn't work out terms",
  deselected: 'was left out of this bulk extension',
  error: 'something went wrong — try again',
};

const playerIdFromRow = (row) => row?.player?._id || row?.player?.id || row?.player;

const playerNameFromRow = (row) => {
  const player = row?.player;
  const nestedName =
    player?.name ||
    [player?.first_name, player?.last_name].filter(Boolean).join(' ');
  return row?.name || row?.player_name || nestedName || 'Player';
};

const playerMetaFromRow = (row, squadById = {}) => {
  const id = playerIdFromRow(row);
  const player = row?.player || {};
  const fromSquad = (id && squadById[id]) || {};
  const age = player.age ?? fromSquad.age;
  const rawPos = player.fav_position ?? fromSquad.fav_position;
  const pos = simplifiedPosition[rawPos] || rawPos || null;
  const parts = [];
  if (pos) parts.push(pos);
  if (age != null && age !== '') parts.push(String(age));
  return parts.join(' · ');
};

const formatMood = (mood) => {
  if (!mood) return '—';
  const label = String(mood).replace(/_/g, ' ');
  return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
};

const formatBand = ([low, high], format = (value) => value) =>
  low === high ? `${format(low)}` : `${format(low)}–${format(high)}`;

// What a skipped player would sign for, from the preview's bands (daily wage, matches, seasons).
const formatWants = (wants) => {
  if (!wants?.wage || !wants?.matches || !wants?.seasons) return null;
  const seasons = wants.seasons[1] === 1 ? 'season' : 'seasons';
  return `Wants $${formatBand(wants.wage, (value) => digitFormater(value, 0))}/day, ${formatBand(
    wants.matches
  )} matches, ${formatBand(wants.seasons)} ${seasons}.`;
};

const formatSkipReason = (row) => {
  const raw = SKIP_COPY[row?.reason] || row?.reason || 'skipped';
  const text = String(raw);
  const sentence = `${text.charAt(0).toUpperCase()}${text.slice(1)}.`;
  const wants = formatWants(row?.wants);
  return wants ? `${sentence} ${wants}` : sentence;
};

export default function AutoContractsPanel({ onContractsRefresh }) {
  const dispatch = useDispatch();
  const isPremium = useSelector(isPremiumUser);
  const squad = useSelector(getMyAllSquad) || [];
  const squadById = useMemo(
    () =>
      Object.fromEntries(
        (Array.isArray(squad) ? squad : [])
          .filter((player) => player?._id)
          .map((player) => [player._id, player])
      ),
    [squad]
  );
  const [settings, setSettings] = useState(null);
  const [availability, setAvailability] = useState('loading');
  const [banner, setBanner] = useState(null);
  const [report, setReport] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedProposalIds, setSelectedProposalIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [chrisDeals, setChrisDeals] = useState([]);

  const load = useCallback(async () => {
    try {
      const autoRes = await getAutoContracts();
      if ([404, 405, 501].includes(autoRes.status)) {
        setAvailability('unavailable');
        return;
      }
      if (autoRes.status === 417) {
        setSettings({ entitled: false });
        setAvailability('ready');
        return;
      }
      if (autoRes.status !== 200) {
        setAvailability('error');
        return;
      }
      setSettings(await autoRes.json());
      setAvailability('ready');
      // Pending deals are supplementary; their failure must not hide settings.
      const financeRes = await getFinanceContracts().catch(() => null);
      if (financeRes?.status === 200) {
        const finance = await financeRes.json();
        setBanner(finance.auto || null);
        const deals = (finance.contracts || [])
          .filter((row) => row.auto_extension)
          .map((row) => ({
            playerId: playerIdFromRow(row) || row.player_id || row._id,
            name: playerNameFromRow(row),
            // The API marks the players this club lent out (loaned_out); any other lender is the
            // club that owns a player on loan here, and only that club can cancel his deal.
            loanedIn: Boolean(row.player?.lender_team && !row.loaned_out),
          }))
          .filter((deal) => deal.playerId);
        setChrisDeals(deals);
      }
    } catch {
      setAvailability('error');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveSettings = async (patch) => {
    if (busy || availability !== 'ready') return;
    setBusy(true);
    try {
      const response = await postAutoContracts(patch);
      if (response.status === 200) {
        const data = await response.json();
        setSettings((prev) => ({ ...prev, ...data, ...patch }));
        toastRender('success', 'Chris settings saved.');
        await load();
      } else if ([404, 405, 501].includes(response.status)) {
        setAvailability('unavailable');
      } else if (response.status === 417) {
        dispatch(
          showPopupWindow(POPUPS.MOBILE_REQUIRED_POPUP, POPUPS.MOBILE_REQUIRED_POPUP)
        );
      } else {
        toastRender('error', 'Could not save auto-contract settings.');
      }
    } catch {
      toastRender('error', 'Could not save auto-contract settings.');
    } finally {
      setBusy(false);
    }
  };

  const runWand = async () => {
    if (busy || availability !== 'ready') return;
    setBusy(true);
    try {
      // Selection has to be part of the bulk request. Running the wand for every
      // proposal and cancelling the unwanted deals afterwards is too late: the
      // server has already persisted the extensions and announced them by then.
      const response = await postAutoContracts({
        wand: true,
        ...(preview ? { players: selectedProposalIds } : {}),
      });
      if (response.status === 200) {
        const data = await response.json();
        setReport(data);
        // The preview described the squad before this run; keeping it would offer deals that
        // are now agreed. Preview again to see what is left.
        setPreview(null);
        setSelectedProposalIds([]);
        toastRender('success', 'Chris finished reviewing selected extensions.');
        await load();
        if (onContractsRefresh) onContractsRefresh();
      } else if ([404, 405, 501].includes(response.status)) {
        setAvailability('unavailable');
      } else if (response.status === 417) {
        dispatch(
          showPopupWindow(POPUPS.MOBILE_REQUIRED_POPUP, POPUPS.MOBILE_REQUIRED_POPUP)
        );
      } else {
        toastRender('error', 'Could not extend contracts.');
      }
    } catch {
      toastRender('error', 'Could not extend contracts.');
    } finally {
      setBusy(false);
    }
  };

  const runPreview = async () => {
    if (busy || availability !== 'ready') return;
    setBusy(true);
    try {
      const response = await getAutoContractsPreview();
      if (response.status === 200) {
        const data = await response.json();
        setPreview(data);
        setSelectedProposalIds(
          (data.proposals || []).map(playerIdFromRow).filter(Boolean)
        );
      } else if ([404, 405, 501].includes(response.status)) {
        setAvailability('unavailable');
      } else if (response.status === 417) {
        dispatch(
          showPopupWindow(POPUPS.MOBILE_REQUIRED_POPUP, POPUPS.MOBILE_REQUIRED_POPUP)
        );
      } else {
        toastRender('error', 'Could not load preview.');
      }
    } catch {
      toastRender('error', 'Could not load preview.');
    } finally {
      setBusy(false);
    }
  };

  const cancelDeal = async (playerId) => {
    if (busy || availability !== 'ready') return;
    setBusy(true);
    try {
      const response = await cancelContractExtension(playerId);
      if (response.status === 200) {
        // The player now shows as cancelled in a fresh preview, where Chris can be asked again.
        setPreview(null);
        setSelectedProposalIds([]);
        toastRender('success', 'Chris extension cancelled.');
        await load();
        if (onContractsRefresh) onContractsRefresh();
      } else if (response.status === 404) {
        // The cancel route exists (settings loaded); 404 means the club no longer holds this
        // player's contract, so refresh rather than hiding the whole panel as Coming soon.
        toastRender('error', 'That player’s contract is no longer with your club.');
        await load();
      } else if ([405, 501].includes(response.status)) {
        setAvailability('unavailable');
      } else if (response.status === 409) {
        toastRender('error', 'Nothing to cancel (or you agreed that deal yourself).');
      } else {
        toastRender('error', 'Could not cancel extension.');
      }
    } catch {
      toastRender('error', 'Could not cancel extension.');
    } finally {
      setBusy(false);
    }
  };

  // A cancelled Chris deal keeps the player out of the squad-wide preview for the season. Naming
  // him asks Chris again: a deal comes back as a proposal (ticked, so Extend selected sends him,
  // which the API also treats as naming him); otherwise his row shows the new reason.
  const askAgain = async (playerId) => {
    if (busy || availability !== 'ready') return;
    setBusy(true);
    try {
      const response = await getAutoContractsPreview(playerId);
      if (response.status === 200) {
        const data = await response.json();
        const proposal = (data.proposals || [])[0];
        const skip = (data.skipped || [])[0];
        setPreview((current) =>
          current && {
            ...current,
            proposals: proposal
              ? [...(current.proposals || []).filter((row) => playerIdFromRow(row) !== playerId), proposal]
              : current.proposals || [],
            skipped: (current.skipped || []).flatMap((row) =>
              playerIdFromRow(row) !== playerId ? [row] : !proposal && skip ? [skip] : []
            ),
          }
        );
        if (proposal) {
          setSelectedProposalIds((current) =>
            current.includes(playerId) ? current : [...current, playerId]
          );
        }
      } else if (response.status === 404) {
        toastRender('error', 'That player’s contract is no longer with your club.');
      } else if (response.status === 417) {
        dispatch(
          showPopupWindow(POPUPS.MOBILE_REQUIRED_POPUP, POPUPS.MOBILE_REQUIRED_POPUP)
        );
      } else {
        toastRender('error', 'Could not ask Chris about this player.');
      }
    } catch {
      toastRender('error', 'Could not ask Chris about this player.');
    } finally {
      setBusy(false);
    }
  };

  const entitled = settings?.entitled ?? banner?.entitled ?? isPremium;
  const enabled = settings?.enabled ?? banner?.enabled ?? false;
  const strategy = settings?.strategy ?? banner?.strategy ?? 0;
  const keepEveryone = settings?.keepEveryone ?? banner?.keepEveryone ?? false;
  const windowInfo = settings?.window;
  const comingSoon = availability === 'unavailable';
  const controlsDisabled = busy || availability !== 'ready';
  const selectedProposalCount = selectedProposalIds.length;
  const proposalIds = (preview?.proposals || []).map(playerIdFromRow).filter(Boolean);
  const allProposalsSelected =
    proposalIds.length > 0 && proposalIds.every((id) => selectedProposalIds.includes(id));

  const toggleProposal = (playerId) => {
    setSelectedProposalIds((current) =>
      current.includes(playerId)
        ? current.filter((id) => id !== playerId)
        : [...current, playerId]
    );
  };

  const toggleAllProposals = () => {
    setSelectedProposalIds(allProposalsSelected ? [] : proposalIds);
  };

  return (
    <StyledAutoCard aria-labelledby="auto-contracts-title" aria-busy={busy || availability === 'loading'}>
      <StyledAutoHeader>
        <StyledAutoIcon aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="m12 3 2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5L12 3Z" />
            <path d="M20 2v4M18 4h4" />
          </svg>
        </StyledAutoIcon>
        <div>
          <StyledAutoTitle id="auto-contracts-title">
            Auto extensions
            <StyledPatronBadge><Crown /> Patron</StyledPatronBadge>
          </StyledAutoTitle>
          <StyledAutoHint>Your contract assistant, Chris.</StyledAutoHint>
        </div>
        <StyledAutoStatus role="status" $active={Boolean(availability === 'ready' && entitled && enabled)}>
          {availability === 'loading' ? 'Checking' : availability === 'unavailable' ? 'Coming soon' : availability === 'error' ? 'Unavailable' : !entitled ? 'Patron' : enabled ? 'Enabled' : 'Off'}
        </StyledAutoStatus>
      </StyledAutoHeader>
      {availability !== 'ready' && !comingSoon ? (
        <StyledAutoFooter role="status">
          <StyledAutoHint>
            {availability === 'loading'
              ? 'Checking whether automatic extensions are available…'
              : availability === 'unavailable'
                ? 'We’re putting the finishing touches on automatic extensions. You can still renew players individually in Squad contracts below.'
                : 'Could not load automatic extensions. Please try again.'}
          </StyledAutoHint>
          {availability === 'error' ? (
            <StyledAutoButton $secondary type="button" onClick={() => {
              setAvailability('loading');
              load();
            }}>
              Try again
            </StyledAutoButton>
          ) : null}
        </StyledAutoFooter>
      ) : !entitled && !comingSoon ? (
        <StyledAutoFooter>
          <StyledAutoHint>
            Let Chris handle renewals from matchday 16. Patron includes automatic
            extensions, a preview of proposed deals and the option to extend now.
          </StyledAutoHint>
          <StyledAutoButton
            type="button"
            onClick={() =>
              dispatch(
                showPopupWindow(
                  POPUPS.MOBILE_REQUIRED_POPUP,
                  POPUPS.MOBILE_REQUIRED_POPUP
                )
              )
            }
          >
            Unlock with Patron
          </StyledAutoButton>
        </StyledAutoFooter>
      ) : (
        <StyledAutoBody $comingSoon={comingSoon}>
          <StyledAutoSettings>
            <StyledAutoField>
              <StyledAutoRow>
                <StyledAutoLabel htmlFor="auto-contracts-enabled">Automatic renewals</StyledAutoLabel>
                <StyledAutoToggle
                  id="auto-contracts-enabled"
                  checked={Boolean(enabled)}
                  disabled={controlsDisabled}
                  onChange={(event) => saveSettings({ enabled: event.target.checked })}
                />
              </StyledAutoRow>
              <StyledAutoHint>Let Chris handle expiring contracts each season.</StyledAutoHint>
            </StyledAutoField>
            <StyledAutoField>
              <StyledAutoRow>
                <StyledAutoLabel htmlFor="auto-contracts-strategy">Negotiation strategy</StyledAutoLabel>
              </StyledAutoRow>
              <StyledAutoSelect
                id="auto-contracts-strategy"
                value={strategy}
                disabled={controlsDisabled}
                onChange={(event) => saveSettings({ strategy: Number(event.target.value) })}
              >
                <option value={0}>Protect wage bill</option>
                <option value={1}>Protect selection</option>
              </StyledAutoSelect>
              <StyledAutoHint>{STRATEGY_BLURBS[strategy] || STRATEGY_BLURBS[0]}</StyledAutoHint>
            </StyledAutoField>
            <StyledAutoField>
              <StyledAutoRow>
                <StyledAutoLabel htmlFor="auto-contracts-keep">Keep everyone</StyledAutoLabel>
                <StyledAutoToggle
                  id="auto-contracts-keep"
                  checked={Boolean(keepEveryone)}
                  disabled={controlsDisabled}
                  onChange={(event) => saveSettings({ keepEveryone: event.target.checked })}
                />
              </StyledAutoRow>
              <StyledAutoHint>Prioritize keeping your squad together.</StyledAutoHint>
            </StyledAutoField>
          </StyledAutoSettings>
          <StyledAutoFooter>
            <StyledAutoHint>
              {comingSoon
                ? 'Available soon. You can still renew players individually in Squad contracts below.'
                : windowInfo?.alreadyRanThisSeason
                ? 'Chris has already reviewed this season’s renewals.'
                : enabled
                  ? `Automatic review from season day ${windowInfo?.runsOn ?? 16}.`
                  : 'Automatic renewals are off. You can still preview deals or extend now.'}
              {windowInfo?.matchday != null ? ` Current season day: ${windowInfo.matchday}.` : ''}
            </StyledAutoHint>
            <StyledAutoActions>
              <StyledAutoButton $secondary type="button" disabled={controlsDisabled} onClick={runPreview}>
                Preview deals
              </StyledAutoButton>
              <StyledAutoButton
                type="button"
                disabled={controlsDisabled || Boolean(preview && !selectedProposalCount)}
                onClick={runWand}
              >
                {preview ? `Extend selected (${selectedProposalCount})` : 'Extend now'}
              </StyledAutoButton>
            </StyledAutoActions>
          </StyledAutoFooter>
        </StyledAutoBody>
      )}

      {availability === 'ready' && chrisDeals.length > 0 ? (
        <StyledAutoResult>
          <StyledAutoHint>Pending Chris extensions</StyledAutoHint>
          <StyledAutoList>
            {chrisDeals.map((deal) => (
              <StyledAutoItem key={deal.playerId || deal.name}>
                <span>{deal.name}</span>
                {!deal.loanedIn ? (
                  <StyledAutoButton
                    type="button"
                    disabled={busy}
                    onClick={() => cancelDeal(deal.playerId)}
                  >
                    Cancel
                  </StyledAutoButton>
                ) : (
                  <span>Owning club only</span>
                )}
              </StyledAutoItem>
            ))}
          </StyledAutoList>
        </StyledAutoResult>
      ) : null}

      {availability === 'ready' && preview ? (
        <StyledAutoResult>
          <StyledPreviewHeader>
            <div>
              <StyledPreviewTitle>Proposed extensions</StyledPreviewTitle>
              <StyledAutoHint>Select the players Chris should extend.</StyledAutoHint>
            </div>
            <StyledPreviewCounts>
              <StyledPreviewCount $active>
                {preview.proposals?.length || 0} proposed
              </StyledPreviewCount>
              {preview.skipped?.length ? (
                <StyledPreviewCount>{preview.skipped.length} skipped</StyledPreviewCount>
              ) : null}
            </StyledPreviewCounts>
          </StyledPreviewHeader>
          <StyledPreviewTableWrap>
            <StyledPreviewTable>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allProposalsSelected}
                      disabled={busy || !proposalIds.length}
                      aria-label="Select all proposed extensions"
                      onChange={toggleAllProposals}
                    />
                  </th>
                  <th>Player</th>
                  <th>Daily wage</th>
                  <th>Guaranteed</th>
                  <th>Length</th>
                  <th>Mood</th>
                </tr>
              </thead>
              <tbody>
                {(preview.proposals || []).map((row) => {
                  const name = playerNameFromRow(row);
                  const meta = playerMetaFromRow(row, squadById);
                  return (
                  <tr key={row.player?._id || name}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedProposalIds.includes(playerIdFromRow(row))}
                        disabled={busy}
                        aria-label={`Extend ${name}`}
                        onChange={() => toggleProposal(playerIdFromRow(row))}
                      />
                    </td>
                    <td>
                      <StyledPreviewPlayer>
                        <span className="name">{name}</span>
                        {meta ? <span className="meta">{meta}</span> : null}
                      </StyledPreviewPlayer>
                    </td>
                    <td>${digitFormater(row.wage, 0)}<small>/day</small></td>
                    <td>{row.playTime} <small>matches</small></td>
                    <td>{row.length} <small>{row.length === 1 ? 'season' : 'seasons'}</small></td>
                    <td><StyledPreviewMood>{formatMood(row.wants?.mood)}</StyledPreviewMood></td>
                  </tr>
                  );
                })}
                {(preview.skipped || []).map((row) => {
                  const playerId = playerIdFromRow(row);
                  const name = playerNameFromRow(row);
                  const meta = playerMetaFromRow(row, squadById);
                  return (
                    <StyledPreviewSkippedRow
                      key={`skip-${playerId || name}`}
                      aria-label={`${name} skipped`}
                    >
                      <td aria-hidden="true" />
                      <td>
                        <StyledPreviewPlayer>
                          <span className="name">{name}</span>
                          {meta ? <span className="meta">{meta}</span> : null}
                        </StyledPreviewPlayer>
                      </td>
                      <td colSpan={4}>
                        <div className="reason">
                          <span>{formatSkipReason(row)}</span>
                          {row.reason === 'cancelled' && playerId ? (
                            <StyledAutoButton
                              $secondary
                              type="button"
                              disabled={busy}
                              aria-label={`Ask Chris again about ${name}`}
                              onClick={() => askAgain(playerId)}
                            >
                              Ask Chris again
                            </StyledAutoButton>
                          ) : null}
                        </div>
                      </td>
                    </StyledPreviewSkippedRow>
                  );
                })}
              </tbody>
            </StyledPreviewTable>
          </StyledPreviewTableWrap>
          {!preview.proposals?.length ? (
            <StyledAutoHint role="status">
              {preview.skipped?.length
                ? 'Chris has no deal to propose right now. Skipped players are listed in red.'
                : 'Nothing to propose. Every player in the last year of their deal already has an extension agreed, or none is in their last year.'}
            </StyledAutoHint>
          ) : null}
        </StyledAutoResult>
      ) : null}

      {availability === 'ready' && report ? (
        <StyledAutoResult>
          <StyledAutoHint>
            Last run — extended {report.extended?.length || 0}, skipped{' '}
            {report.skipped?.length || 0}
            {report.wageBill
              ? ` · wage bill ${digitFormater(report.wageBill.before, 0)} → ${digitFormater(
                  report.wageBill.after,
                  0
                )} / ${digitFormater(report.wageBill.limit, 0)}`
              : ''}
          </StyledAutoHint>
          <StyledAutoList>
            {(report.extended || []).map((row) => (
              <StyledAutoItem key={`e-${row.player?._id}`}>
                <span>
                  Extended {row.player?.name || 'Player'} ({row.where})
                </span>
              </StyledAutoItem>
            ))}
            {(report.skipped || []).map((row) => (
              <StyledAutoItem key={`s-${row.player?._id}`}>
                <span>
                  Skipped {row.player?.name || 'Player'}:{' '}
                  {SKIP_COPY[row.reason] || row.reason}
                </span>
              </StyledAutoItem>
            ))}
          </StyledAutoList>
        </StyledAutoResult>
      ) : null}
    </StyledAutoCard>
  );
}
